import React, { useCallback, useMemo } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { ExplanationTooltip, Flex, FlexCenter } from '@ubeya/shared-web/components';
import { AvailabilityWindowStatus } from '@ubeya/shared/utils/resolveAvailabilityWindow';
import PlusButton from '../PlusButton';
import { TimeAvailabilityItemChip } from './TimeAvailabilityItemChip';
import { TimeOffItemChip } from './TimeOffItemChip';

const ShiftsWrapper = styled.div`
  .plus-button {
    visibility: visible;
    margin-right: 20px;
  }
`;

const Container = styled.div``;

const DayStrip = styled.div`
  background-color: ${({ theme }) => theme.colors.grey1};
  padding: 8px 32px;
`;

const DayTitle = styled.div`
  font-weight: 500;
  width: 100%;
  font-size: 14px;
  color: ${({ theme, today }) => (today ? theme.colors.primary : theme.colors.black)};
`;

const DayStripContent = styled(Flex)``;

const EmptyTitle = styled.div`
  margin-left: 35px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.black};
  flex: 1;
`;

const Chips = styled(FlexCenter)`
  flex: 1;
  flex-wrap: wrap;
  margin-left: 10px;
  justify-content: start;
  padding: 8px 12px;
`;

const Content = styled(FlexCenter)`
  align-items: center;
  min-height: 60px;
`;

export const TimeAvailabilityDay = ({
  t,
  date,
  dateFormat,
  mappedTimeOffRequestOptions,
  periods,
  onCreateRequestClicked,
  onDeletePeriod,
  setSelectedDay,
  availabilityWindowStatus,
  dailyAvailability
}) => {
  const today = useMemo(() => moment(date).isSame(moment(), 'date'), [date]);

  const getGeneralAvailabilityLabel = useCallback(
    (type) => (type === 'Not Available' ? t('notAvailable') : t('available')),
    [t]
  );

  const onClick = useCallback(() => {
    setSelectedDay(date);
    onCreateRequestClicked();
  }, [date, onCreateRequestClicked, setSelectedDay]);

  const dayTitle = (
    <DayTitle today={today}>
      {moment(date).format(`ddd ${dateFormat}`).toUpperCase()} {today ? ` (${t('today')})` : ''}
    </DayTitle>
  );

  return (
    <Container>
      <DayStrip>
        <DayStripContent>
          {availabilityWindowStatus ? (
            <ExplanationTooltip
              title={dayTitle}
              body={t(
                availabilityWindowStatus.status === AvailabilityWindowStatus.LOCKED
                  ? 'availabilityWindowLockedDayTooltip'
                  : 'availabilityWindowNotOpenYetDayTooltip'
              )}
              maxWidth={280}
            />
          ) : (
            dayTitle
          )}
        </DayStripContent>
      </DayStrip>

      <Content>
        {(periods || []).length === 0 ? (
          <EmptyTitle>
            {dailyAvailability?.source === 'general-availability' || dailyAvailability?.source === 'account-default'
              ? getGeneralAvailabilityLabel(dailyAvailability.type)
              : t('timeAvailabilityEmptyDay')}
          </EmptyTitle>
        ) : (
          <Chips>
            {periods.map(({ id, startTime, endTime, type, timeoffId, jobTypeId, status }) =>
              type === 'availability' ? (
                <TimeAvailabilityItemChip
                  key={id}
                  id={id}
                  startTime={startTime}
                  endTime={endTime}
                  status={status}
                  onDeletePeriod={onDeletePeriod}
                />
              ) : (
                <TimeOffItemChip
                  key={timeoffId}
                  id={timeoffId}
                  jobTypeId={jobTypeId}
                  mappedTimeOffRequestOptions={mappedTimeOffRequestOptions}
                  onDeletePeriod={onDeletePeriod}
                />
              )
            )}
          </Chips>
        )}
        <ShiftsWrapper onClick={onClick}>
          <PlusButton className="plus" />
        </ShiftsWrapper>
      </Content>
    </Container>
  );
};
